import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { MessageSquare, Clock, Loader2, Zap, Repeat } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../App";
import { isIOSApp } from "../../hooks/useIsMobileApp";

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

export const TopUpDialog = ({ open, onOpenChange, usageType = null, onPurchased }) => {
  const [packages, setPackages] = useState([]);
  const [addonsInfo, setAddonsInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [buying, setBuying] = useState(null);
  const [filter, setFilter] = useState(usageType || "all");
  const [mode, setMode] = useState("monthly"); // "monthly" | "onetime"

  useEffect(() => {
    if (!open) return;
    setFilter(usageType || "all");
    setLoading(true);
    Promise.all([
      axios.get(`${API}/usage/packages`, getAuthConfig()),
      axios.get(`${API}/usage/addons`, getAuthConfig()).catch(() => ({ data: null })),
    ])
      .then(([pkgs, addons]) => {
        setPackages(pkgs.data.packages || []);
        setAddonsInfo(addons.data);
        setMode(addons.data?.recurring_available ? "monthly" : "onetime");
      })
      .catch(() => toast.error("Failed to load top-up packages"))
      .finally(() => setLoading(false));
  }, [open, usageType]);

  const recurring = !!addonsInfo?.recurring_available;

  // No usage/add-on purchases inside the iOS app (Apple Guideline 3.1.1)
  if (isIOSApp()) return null;
  const monthly = recurring && mode === "monthly";
  const activeIds = addonsInfo?.active_package_ids || [];

  const subscribe = async (pkg) => {
    setBuying(pkg.package_id);
    try {
      const res = await axios.post(`${API}/usage/addons/subscribe`, { package_id: pkg.package_id }, getAuthConfig());
      if (res.data.status === "active") {
        toast.success(`Add-on active: +${Number(pkg.quantity).toLocaleString()} ${pkg.usage_type === "sms" ? "SMS" : "minutes"}/month${res.data.charged_now != null ? ` — $${Number(res.data.charged_now).toFixed(2)} charged now (prorated)` : ""}`);
      } else {
        toast.info("Payment is processing — the add-on activates automatically once confirmed.");
      }
      onPurchased?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Could not add the add-on");
    } finally {
      setBuying(null);
    }
  };

  const buyOneTime = async (pkg) => {
    setBuying(pkg.package_id);
    try {
      const res = await axios.post(`${API}/usage/topup/checkout`, {
        package_id: pkg.package_id,
        origin_url: window.location.origin,
      }, getAuthConfig());
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Failed to start checkout");
      setBuying(null);
    }
  };

  const shown = packages.filter(p => filter === "all" || p.usage_type === filter);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="topup-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#FFCC00]" /> Buy More Usage
          </DialogTitle>
          <DialogDescription>
            {monthly
              ? "Add-ons renew monthly with your subscription — same renewal date, same invoice, 5% off. Your first charge is prorated for the remainder of this billing period."
              : "One-time packages stay on your account until you've used them up — no expiry, no renewal."}
          </DialogDescription>
        </DialogHeader>

        {recurring && (
          <div className="grid grid-cols-2 gap-2" data-testid="purchase-mode-toggle">
            <button
              onClick={() => setMode("monthly")}
              data-testid="mode-monthly"
              className={`rounded-lg border p-2.5 text-left transition-colors ${
                mode === "monthly" ? "border-[#0046BE] bg-[#0046BE]/10" : "border-border hover:border-[#0046BE]/50"
              }`}
            >
              <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <Repeat className="w-3.5 h-3.5 text-[#0046BE]" /> Monthly add-on
                <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[9px] px-1.5">5% OFF</Badge>
              </span>
              <span className="block text-[10px] text-muted-foreground mt-0.5">Renews automatically with your plan</span>
            </button>
            <button
              onClick={() => setMode("onetime")}
              data-testid="mode-onetime"
              className={`rounded-lg border p-2.5 text-left transition-colors ${
                mode === "onetime" ? "border-[#0046BE] bg-[#0046BE]/10" : "border-border hover:border-[#0046BE]/50"
              }`}
            >
              <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <Zap className="w-3.5 h-3.5 text-[#FFCC00]" /> One-time
              </span>
              <span className="block text-[10px] text-muted-foreground mt-0.5">Pay once — yours until used up</span>
            </button>
          </div>
        )}

        <div className="flex gap-2 mb-1">
          {["all", "sms", "minutes"].map(f => (
            <button
              key={f}
              data-testid={`topup-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                filter === f ? "bg-[#0046BE] text-white border-[#0046BE]" : "bg-transparent text-muted-foreground border-border hover:text-foreground"
              }`}
            >
              {f === "all" ? "All" : f === "sms" ? "SMS" : "Minutes"}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : shown.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No packages available for your plan.</p>
        ) : (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
            {shown.map(pkg => {
              const isActive = monthly && activeIds.includes(pkg.package_id);
              const subPrice = pkg.subscription_price ?? Math.round(pkg.price * 95) / 100;
              return (
                <div key={pkg.package_id}
                  className="flex items-center justify-between rounded-lg border border-border bg-card/60 p-3"
                  data-testid={`topup-package-${pkg.package_id}`}>
                  <div className="flex items-center gap-3">
                    {pkg.usage_type === "sms"
                      ? <MessageSquare className="w-4 h-4 text-purple-500" />
                      : <Clock className="w-4 h-4 text-orange-500" />}
                    <div>
                      <p className="text-sm font-semibold text-foreground">{pkg.name}</p>
                      <p className="text-[11px] text-muted-foreground">
                        +{Number(pkg.quantity).toLocaleString()} {pkg.usage_type === "sms" ? "SMS" : "minutes"}
                        {monthly ? " · every month" : " · until used up"}
                        {monthly && <span className="text-muted-foreground/70 line-through ml-1.5">${Number(pkg.price).toFixed(2)}</span>}
                      </p>
                    </div>
                  </div>
                  {isActive ? (
                    <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[10px]" data-testid={`topup-active-${pkg.package_id}`}>
                      <Repeat className="w-3 h-3 mr-1" /> Active
                    </Badge>
                  ) : (
                    <Button size="sm" disabled={!!buying}
                      onClick={() => (monthly ? subscribe(pkg) : buyOneTime(pkg))}
                      data-testid={`topup-buy-${pkg.package_id}`}
                      className="bg-[#0046BE] hover:bg-[#0046BE]/90 text-white">
                      {buying === pkg.package_id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : monthly ? `$${Number(subPrice).toFixed(2)}/mo` : `$${Number(pkg.price).toFixed(2)}`}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground">
          {monthly
            ? "Charged to your subscription's payment method. Cancel any time from My Plan — you keep what you paid for until the period ends."
            : "Payments are processed securely by Stripe. Usage is credited only after a confirmed payment and never expires."}
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default TopUpDialog;
